/* Attendance — the church's side, at a glance.
 *
 *   Today          each service on today, how many are in, and the roll
 *   Services       how each weekly service has gone, day by day
 *   Look in on     who missed the last service, longest absence first
 *
 * Ushers (attendance.mark) see Today and the trends; whoever manages
 * attendance also sees who to look in on. The server checks again on every
 * call. The roll and the full list of days are pages of their own.
 */
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, CalendarDays, ChevronRight, ClipboardCheck, Loader2 } from 'lucide-react'
import { useFollowUp, useMe, useMyPermissions, useOccurrences } from '@/api/queries'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { Occurrence } from '@/types/api'
import { FollowUpActions } from './DayReportPage'
import { type Back, DayRow, OnNowChip, TrendBars, averageRate, percent, rate } from './DayList'
import { MarkPresent } from './MarkPresent'
import { Pager, clock, day, familyNote, todayIso } from './parts'

export function useAttendanceAccess(churchId: string | null) {
  const { data: me } = useMe()
  const permissions = useMyPermissions(churchId)
  const admin = me?.role === 'church_admin' || me?.role === 'super_admin'
  const has = (p: string) => admin || (permissions.data?.includes(p) ?? false)
  const manage = has('attendance.manage')
  return {
    loading: permissions.isLoading,
    manage,
    mark: manage || has('attendance.mark'),
  }
}

const FROM_DASHBOARD: Back = { back: '/attendance', backLabel: 'Attendance' }

/** Every attendance page starts the same way: a church, and the right to
 *  keep its attendance. Returns the church, or what to show instead. */
function useGate() {
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null
  const access = useAttendanceAccess(churchId)

  let blocked: React.ReactNode = null
  if (!me) blocked = <></>
  else if (!churchId) {
    blocked = (
      <Card className="p-s6 text-center text-base text-ink-2">
        Attendance belongs to a church. Join one first.
      </Card>
    )
  } else if (access.loading) {
    blocked = (
      <Card className="flex justify-center py-s6">
        <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
      </Card>
    )
  } else if (!access.mark) {
    blocked = (
      <Card className="p-s6 text-center text-base text-ink-2">
        Attendance is kept by your church’s welcome team and leaders.
      </Card>
    )
  }
  return { churchId, access, blocked }
}

export function AttendancePage() {
  const { churchId, access, blocked } = useGate()
  // Enough days to draw the last eight of each of a few weekly services.
  const recent = useOccurrences(churchId, { pageSize: 60 }, Boolean(churchId) && access.mark)

  if (blocked || !churchId) return blocked
  const items = recent.data?.items ?? []

  return (
    <div className="space-y-s5">
      <header className="flex flex-wrap items-end gap-s3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold tracking-tight">Attendance</h1>
          <p className="mt-0.5 text-base text-ink-2">Who came, and who to look in on.</p>
        </div>
        <Button asChild variant="secondary">
          <Link to="/attendance/days">
            <CalendarDays className="h-4 w-4" aria-hidden />
            All service days
          </Link>
        </Button>
      </header>

      {recent.isLoading ? (
        <Card className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </Card>
      ) : recent.isError ? (
        <Card className="p-s6 text-center text-base text-ink-2">
          {(recent.error as Error).message}
        </Card>
      ) : !items.length ? (
        <Card className="p-s8 text-center">
          <CalendarDays className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">No service days yet</p>
          <p className="mt-1 text-base text-ink-2">
            {access.manage ? (
              <>
                Add a weekly service under{' '}
                <Link to="/services?tab=services" className="font-semibold text-brand hover:underline">
                  Services
                </Link>
                , and each day it happens shows here.
              </>
            ) : (
              'Once a weekly service is set up, each day it happens shows here.'
            )}
          </p>
        </Card>
      ) : (
        <>
          <Today items={items} />
          <ServiceTrends items={items} />
          {access.manage ? <LookInOn churchId={churchId} items={items} /> : null}
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ today */

function Today({ items }: { items: Occurrence[] }) {
  const today = todayIso()
  const on = items.filter((o) => o.date === today)
  // Nothing today: the roll still opens on the latest day, for marking
  // somebody the usher missed.
  const latest = items[0]

  return (
    <section className="space-y-s3">
      <h2 className="text-md font-bold">Today</h2>
      {!on.length ? (
        <Card className="flex flex-wrap items-center gap-s3 p-s4">
          <p className="min-w-0 flex-1 text-base text-ink-2">
            No service today. The last was {latest.name}, {day(latest.date, true)}.
          </p>
          <Button asChild variant="secondary">
            <Link to={`/attendance/mark?day=${latest.id}`}>
              <ClipboardCheck className="h-4 w-4" aria-hidden />
              Open its roll
            </Link>
          </Button>
        </Card>
      ) : (
        <div className="grid gap-s3 md:grid-cols-2">
          {on.map((o) => (
            <TodayCard key={o.id} occurrence={o} />
          ))}
        </div>
      )}
    </section>
  )
}

function TodayCard({ occurrence: o }: { occurrence: Occurrence }) {
  const c = o.counts
  const share = c.expected > 0 ? Math.min(1, c.present / c.expected) : 0
  const outstanding = o.state === 'over' ? c.absent : c.not_yet
  return (
    <Card className="space-y-s3 p-s4">
      <div>
        <p className="flex flex-wrap items-center gap-s2 text-md font-bold">
          {o.name}
          <OnNowChip state={o.state} />
        </p>
        <p className="text-sm text-ink-2">
          {clock(o.starts_at)} – {clock(o.ends_at)}
          {o.state === 'over' ? ' · over' : ''}
        </p>
      </div>

      <div>
        <p className="text-base">
          <strong className="text-2xl font-extrabold tracking-tight">{c.present}</strong>
          <span className="text-ink-2"> of {c.expected} in</span>
        </p>
        <div className="mt-s2 h-2 overflow-hidden rounded-pill bg-surface-2" aria-hidden>
          <div className="h-full rounded-pill bg-success" style={{ width: `${share * 100}%` }} />
        </div>
        <p className="mt-s2 text-sm text-ink-2">
          {[
            `${outstanding} ${o.state === 'over' ? 'absent' : 'not yet in'}`,
            c.excused ? `${c.excused} excused` : null,
            c.visitors ? `${c.visitors} visitors` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      <div className="flex flex-wrap gap-s2">
        <Button asChild variant="primary">
          <Link to={`/attendance/mark?day=${o.id}`}>
            <ClipboardCheck className="h-4 w-4" aria-hidden />
            Mark present
          </Link>
        </Button>
        <Button asChild variant="secondary">
          <Link to={`/attendance/days/${o.id}`} state={FROM_DASHBOARD}>
            See the day
          </Link>
        </Button>
      </div>
    </Card>
  )
}

/* --------------------------------------------------------------- services */

const TREND = 8

function ServiceTrends({ items }: { items: Occurrence[] }) {
  const byService = useMemo(() => {
    const groups = new Map<string, Occurrence[]>()
    for (const o of items) {
      const list = groups.get(o.service_id) ?? []
      if (list.length < TREND) list.push(o)
      groups.set(o.service_id, list)
    }
    return [...groups.values()]
  }, [items])

  return (
    <section className="space-y-s3">
      <h2 className="text-md font-bold">Services</h2>
      <div className="grid gap-s3 md:grid-cols-2">
        {byService.map((days) => (
          <ServiceTrend key={days[0].service_id} days={days} />
        ))}
      </div>
    </section>
  )
}

function ServiceTrend({ days }: { days: Occurrence[] }) {
  const first = days[0]
  const last = days.find((o) => o.state === 'over')
  const avg = averageRate(days)
  const held = days.filter((o) => o.state === 'over').length
  return (
    <Card className="space-y-s3 p-s4">
      <Link
        to={`/services/${first.service_id}`}
        className="group flex items-center gap-1 text-md font-bold hover:text-brand"
      >
        <span className="truncate">{first.name}</span>
        <ChevronRight className="h-4 w-4 shrink-0 text-ink-3 group-hover:text-brand" aria-hidden />
      </Link>
      <p className="text-sm text-ink-2">
        {last
          ? `${last.counts.present} of ${last.counts.expected} on ${day(last.date)} (${percent(rate(last))})`
          : 'Not held yet'}
        {held > 1 ? ` · ${percent(avg)} on average over ${held}` : ''}
      </p>
      <TrendBars days={days} from={FROM_DASHBOARD} height={64} />
    </Card>
  )
}

/* ------------------------------------------------------------- look in on */

const LOOK_IN_ON = 6

/** The absentees of the latest finished day of each service, the people
 *  who have been away longest first. */
function LookInOn({ churchId, items }: { churchId: string; items: Occurrence[] }) {
  const finished = useMemo(() => {
    const seen = new Set<string>()
    const out: Occurrence[] = []
    for (const o of items) {
      if (o.state !== 'over' || seen.has(o.service_id)) continue
      seen.add(o.service_id)
      out.push(o)
    }
    return out
  }, [items])
  const [chosenId, setChosenId] = useState<string | null>(null)
  // Open on the day with the most people to look in on.
  const busiest = finished.reduce<Occurrence | undefined>(
    (best, o) => (!best || o.counts.absent > best.counts.absent ? o : best),
    undefined,
  )
  const chosen = finished.find((o) => o.id === chosenId) ?? busiest

  const list = useFollowUp(
    churchId,
    chosen?.id,
    { kind: 'absent', page: 1, pageSize: 100 },
    Boolean(chosen),
  )
  const rows = useMemo(
    () =>
      [...(list.data?.items ?? [])]
        .sort((a, b) => b.missed_in_a_row - a.missed_in_a_row)
        .slice(0, LOOK_IN_ON),
    [list.data],
  )

  if (!chosen) return null
  const total = list.data?.total ?? 0

  return (
    <section className="space-y-s3">
      <h2 className="text-md font-bold">Look in on</h2>
      <Card className="space-y-s3 p-s4">
        {finished.length > 1 ? (
          <div role="tablist" className="flex flex-wrap gap-s2">
            {finished.map((o) => (
              <button
                key={o.id}
                role="tab"
                aria-selected={o.id === chosen.id}
                onClick={() => setChosenId(o.id)}
                className={cn(
                  'rounded-pill border px-s3 py-1.5 text-sm font-semibold transition-colors',
                  o.id === chosen.id
                    ? 'border-brand bg-brand-soft text-brand'
                    : 'border-line-2 text-ink-2 hover:bg-hover',
                )}
              >
                {o.name} · {day(o.date)}
              </button>
            ))}
          </div>
        ) : null}

        {list.isLoading ? (
          <div className="flex justify-center py-s4">
            <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
          </div>
        ) : list.isError ? (
          <p className="text-base text-ink-2">{(list.error as Error).message}</p>
        ) : !total ? (
          <p className="text-base text-ink-2">
            Everybody expected at {chosen.name} on {day(chosen.date)} came, or was excused.
          </p>
        ) : (
          <>
            <p className="text-base text-ink-2">
              {total} {total === 1 ? 'person' : 'people'} missed {chosen.name} on{' '}
              {day(chosen.date, true)}.
            </p>
            <ul className="divide-y divide-line-2 rounded border border-line-2">
              {rows.map((p) => {
                const note = familyNote(p.household_of, p.relation)
                return (
                  <li key={p.id} className="flex items-center gap-s3 px-s3 py-s2">
                    <Avatar name={p.name} src={p.avatar_url} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-semibold">{p.name}</p>
                      {note || p.mobile ? (
                        <p className="truncate text-sm text-ink-2">
                          {[note, p.mobile].filter(Boolean).join(' · ')}
                        </p>
                      ) : null}
                    </div>
                    {p.missed_in_a_row >= 2 ? (
                      <span className="shrink-0 rounded-pill bg-warning-soft px-s2 py-0.5 text-xs font-semibold text-warning-ink">
                        Missed last {p.missed_in_a_row}
                      </span>
                    ) : null}
                  </li>
                )
              })}
            </ul>
            <div className="flex flex-wrap items-start gap-s2">
              <div className="min-w-0 flex-1">
                <FollowUpActions
                  churchId={churchId}
                  report={{ id: chosen.id, name: chosen.name, date: chosen.date, over: true }}
                  kind="absent"
                  count={total}
                />
              </div>
              <Button asChild variant="ghost">
                <Link to={`/attendance/days/${chosen.id}`} state={FROM_DASHBOARD}>
                  {total > rows.length ? `See all ${total}` : 'See the day'}
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </Link>
              </Button>
            </div>
          </>
        )}
      </Card>
    </section>
  )
}

/* -------------------------------------------------------- pages of their own */

function BackTo({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink">
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      {label}
    </Link>
  )
}

/** The usher's roll, on the day the link names or the one that is on. */
export function MarkPresentPage() {
  const { churchId, blocked } = useGate()
  const [params] = useSearchParams()
  if (blocked || !churchId) return blocked
  return (
    <div className="space-y-s4">
      <div>
        <BackTo to="/attendance" label="Attendance" />
        <h1 className="mt-s2 text-xl font-extrabold tracking-tight">Mark present</h1>
      </div>
      <MarkPresent churchId={churchId} initialDay={params.get('day')} />
    </div>
  )
}

const DAYS_PAGE = 20

/** Every day held, newest first, each with its report. */
export function ServiceDaysPage() {
  const { churchId, access, blocked } = useGate()
  const [page, setPage] = useState(1)
  const days = useOccurrences(churchId, { page, pageSize: DAYS_PAGE }, Boolean(churchId) && access.mark)
  if (blocked || !churchId) return blocked
  const data = days.data
  const from: Back = { back: '/attendance/days', backLabel: 'Service days' }

  return (
    <div className="space-y-s4">
      <div>
        <BackTo to="/attendance" label="Attendance" />
        <h1 className="mt-s2 text-xl font-extrabold tracking-tight">Service days</h1>
      </div>
      {days.isLoading ? (
        <Card className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </Card>
      ) : days.isError ? (
        <Card className="p-s6 text-center text-base text-ink-2">
          {(days.error as Error).message}
        </Card>
      ) : !data?.items.length ? (
        <Card className="p-s8 text-center text-base text-ink-2">No service days yet.</Card>
      ) : (
        <div className="space-y-s3">
          <Card className={cn('divide-y divide-line-2 overflow-hidden', days.isFetching && 'opacity-70')}>
            {data.items.map((o) => (
              <DayRow key={o.id} occurrence={o} from={from} />
            ))}
          </Card>
          <Pager page={page} total={data.total} pageSize={DAYS_PAGE} onPage={setPage} />
        </div>
      )}
    </div>
  )
}
